import React, { useState, useRef, useEffect } from 'react'

// ──────────────────────────────────────────────────────────────────────────
// Stepper del wizard de convocatoria (Nueva convocatoria > Masivo).
// Adaptado del componente equivalente de gestionNuevosIngresos/ficha/globalComponents/Stepper.jsx
// (misma visualización: círculos numerados unidos por una línea, etiqueta debajo,
// resaltado en verde para el paso activo/completado) para no acoplar este módulo
// a un componente que vive dentro de la carpeta de otra feature.
//
// Diferencias de comportamiento respecto al original:
// - Se puede volver a cualquier paso YA ALCANZADO haciendo clic en su círculo (no
//   existe aquí el concepto de "ficha observada" que gatillaba la navegación en el
//   original). "Ya alcanzado" es `maxStepNumber` (24/09/2026: antes se comparaba
//   contra `currentStepNumber`, que cambia con cada clic — eso hacía que, al volver
//   a un paso anterior, ya no se pudiera volver a avanzar a uno posterior ya
//   visitado. `maxStepNumber` es fijo mientras no cambie la etapa real del
//   postulante, independiente de qué paso se esté mostrando en este momento).
// - `disabledSteps` (24/09/2026): pasos que no aplican al proceso del postulante
//   (p.ej. Evaluación Académica/Clase Modelo/Entrevista en Traslado) y por eso
//   quedan siempre deshabilitados y en gris, sin importar `maxStepNumber`.
// ──────────────────────────────────────────────────────────────────────────
const Stepper = ({ steps, currentStepNumber, setCurrentStepNumber, maxStepNumber, disabledSteps = [], getStepTitle }) => {
    // Si no se pasa maxStepNumber, se asume que se puede navegar hasta donde el
    // usuario ya esté parado (comportamiento previo, por compatibilidad).
    const maxAlcanzado = maxStepNumber ?? currentStepNumber;
    const [stepperSteps, setStep] = useState([]);
    const stepsStateRef = useRef();

    useEffect(() => {
        const stepsState = steps.map((step, index) => {
            const stepObj = {};
            stepObj.description = step;
            stepObj.completed = false;
            stepObj.highlighted = index === 0 ? true : false;
            stepObj.selected = index === 0 ? true : false;
            return stepObj;
        });
        stepsStateRef.current = stepsState;
        const currentSteps = updateStep(currentStepNumber - 1, stepsState)
        setStep(currentSteps)
    }, []);

    useEffect(() => {
        const currentSteps = updateStep(currentStepNumber - 1, stepsStateRef.current)
        setStep(currentSteps)
    }, [currentStepNumber]);

    function updateStep(stepNumber, steps) {
        const newSteps = [...steps];
        let stepCounter = 0;
        while (stepCounter < newSteps.length) {
            if (stepCounter === stepNumber) {
                newSteps[stepCounter] = {
                    ...newSteps[stepCounter],
                    highlighted: true,
                    selected: true,
                    completed: false
                };
                stepCounter++;
            }
            else if (stepCounter < stepNumber) {
                newSteps[stepCounter] = {
                    ...newSteps[stepCounter],
                    highlighted: false,
                    selected: true,
                    completed: true
                };
                stepCounter++;
            }
            else {
                newSteps[stepCounter] = {
                    ...newSteps[stepCounter],
                    highlighted: false,
                    selected: false,
                    completed: false
                }
                stepCounter++;
            }
        }
        return newSteps
    }

    // Se puede navegar a cualquier paso YA ALCANZADO (stepNumber <= maxAlcanzado)
    // que no esté explícitamente deshabilitado. No se puede saltar hacia adelante
    // más allá de la etapa real del postulante.
    const handleClickStep = (step, index) => {
        const stepNumber = index + 1;
        if (stepNumber <= maxAlcanzado && !disabledSteps.includes(stepNumber)) {
            setCurrentStepNumber(stepNumber);
        }
    }

    const stepsDisplay = stepperSteps.map((step, index) => {
        const stepNumber = index + 1;
        const estaDeshabilitado = disabledSteps.includes(stepNumber);
        const canClick = !estaDeshabilitado && stepNumber <= maxAlcanzado;
        // Gris para pasos deshabilitados (no aplican al tipo de proceso del
        // postulante); verde institucional para el resto, como antes.
        const colorStep = estaDeshabilitado ? "#adb5bd" : "#8DBA38";
        const estaSeleccionado = step.selected && !estaDeshabilitado;
        // Texto de ayuda opcional al pasar el cursor (los otros wizards no lo usan).
        const tituloPaso = getStepTitle ? getStepTitle(stepNumber, { canClick, estaDeshabilitado }) : undefined;
        return (
            <div key={index}
                className={`d-flex align-items-center ${index !== stepperSteps.length - 1 ? "w-100 " : ""}`} >
                <div className={`position-relative d-flex flex-column align-items-center ${canClick ? "hand-pointer" : ""}`} style={{color: colorStep}} title={tituloPaso} onClick={() => handleClickStep(step, index)}>
                    <div className={`rounded-circle d-flex align-items-center justify-content-center py-3 ${estaSeleccionado ? "green-innova-background text-white font-weight-bold" : ""}`} style={{border: `2px solid ${colorStep}`, height: "48px", width: "48px"}}>
                        { index + 1 }
                    </div>
                    <div className={`position-absolute top-0 text-center mt-5 ${step.highlighted ? "font-weight-bold" : "font-weight-normal"}`} style={{fontSize: "14px"}}>{step.description}</div>
                </div>
                <div className="" style={{flex: "auto", borderTop: "2px solid gray", transition: "500", transitionTimingFunction: "ease-in-out"}}> </div>
            </div>
        )
    })

    return (
        <div className="mx-4 mb-5 p-4 d-flex justify-content-between align-items-center">
            {stepsDisplay}
        </div>
    )
}

export default Stepper
